/**
 * useNavPrefsStore — per-device nav preferences (localStorage via Zustand persist).
 * Mirrors the persistence pattern in useThemeStore.js.
 *
 *  - bookmarks:    route paths the user pinned to the bar (1 = chip, 2+ = "Bookmarks" menu)
 *  - flerpieHidden: user opted to hide the Flerpie assistant (read by App.js FlerpieWrapper)
 *  - recents:      most-recent-first visited routes, for mobile "Jump back in"
 *
 * Server-backed sync (like useDashboardConfigStore /users/me) is a later upgrade.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const RECENTS_MAX = 5;

export interface NavPrefsState {
  bookmarks: string[];
  flerpieHidden: boolean;
  recents: string[];
  toggleBookmark: (to: string) => void;
  isBookmarked: (to: string) => boolean;
  setFlerpieHidden: (v: boolean) => void;
  pushRecent: (to: string) => void;
}

const useNavPrefsStore = create<NavPrefsState>()(
  persist(
    (set, get) => ({
      bookmarks: [],
      flerpieHidden: false,
      recents: [],
      toggleBookmark: (to) =>
        set((s) => ({
          bookmarks: s.bookmarks.includes(to)
            ? s.bookmarks.filter((b) => b !== to)
            : [...s.bookmarks, to],
        })),
      isBookmarked: (to) => get().bookmarks.includes(to),
      setFlerpieHidden: (v) => set({ flerpieHidden: !!v }),
      pushRecent: (to) =>
        set((s) => ({
          recents: [to, ...s.recents.filter((r) => r !== to)].slice(0, RECENTS_MAX),
        })),
    }),
    { name: 'firefleet.nav-prefs', version: 0, storage: createJSONStorage(() => localStorage) },
  ),
);

export default useNavPrefsStore;
